import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Form, Button, Card, Alert, Spinner, Image as BootstrapImage, ProgressBar } from 'react-bootstrap'; // Added Image and ProgressBar
import { createBook } from '../api/readerService'; // Your API service
import axiosInstance from '../utils/axios'; // For direct image upload
import { UploadCloud, Image as ImageIcon } from 'lucide-react'; // Icons

const CreateBookPage = () => {
    const [title, setTitle] = useState('');
    const [author, setAuthor] = useState('');
    const [description, setDescription] = useState('');
    
    // This will now hold the URL from upload OR manual input
    const [coverImageUrl, setCoverImageUrl] = useState(''); 
    
    const [selectedFile, setSelectedFile] = useState(null); // Holds the File object
    const [previewUrl, setPreviewUrl] = useState(null); // For local image preview
    const [isUploadingCover, setIsUploadingCover] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [coverUploadError, setCoverUploadError] = useState(null);

    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    const handleFileChange = async (event) => {
        const file = event.target.files[0];
        if (file) {
            setSelectedFile(file);
            setPreviewUrl(URL.createObjectURL(file)); // Show local preview
            setCoverImageUrl(''); // Clear any manually entered URL
            setCoverUploadError(null); // Clear previous upload error

            // Automatically upload the selected file
            const formData = new FormData();
            formData.append('image', file); // Key 'image' must match backend
            setIsUploadingCover(true);
            setUploadProgress(0);

            try {
                const response = await axiosInstance.post('/media/upload-image/', formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                    onUploadProgress: (progressEvent) => {
                        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                        setUploadProgress(percentCompleted);
                    },
                });
                setCoverImageUrl(response.data.imageUrl); // Set the URL from backend
                // setSelectedFile(null); // Optionally clear selected file state after successful upload
                // setPreviewUrl(response.data.imageUrl); // Or update preview to the server URL
            } catch (uploadErr) {
                console.error("Cover image upload failed:", uploadErr);
                setCoverUploadError(uploadErr.response?.data?.error || "Cover image upload failed. Please try again.");
                setSelectedFile(null); // Clear selected file on error
                setPreviewUrl(null);   // Clear preview on error
            } finally {
                setIsUploadingCover(false);
                setUploadProgress(0); // Reset progress
            }
        } else {
            setSelectedFile(null);
            setPreviewUrl(null);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (isUploadingCover) {
            alert("Please wait for the cover image to finish uploading.");
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const bookData = { 
                title, 
                author, 
                description, 
                cover_image_url: coverImageUrl // This will be the uploaded URL or manually entered one
            };
            const response = await createBook(bookData);
            navigate(`/book/${response.data.id}`);
        } catch (err) {
            const errorData = err.response?.data;
            if (typeof errorData === 'object' && errorData !== null) {
                // Try to extract a meaningful message
                const messages = Object.values(errorData).flat().join(' ');
                setError(messages || "Failed to create book.");
            } else {
                setError(errorData || "Failed to create book.");
            }
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Container className="py-4" style={{ maxWidth: '700px' }}>
            <Card className="shadow-lg">
                <Card.Header as="h2" className="text-center bg-primary text-white">Create New Book</Card.Header>
                <Card.Body className="p-4">
                    {error && <Alert variant="danger">{typeof error === 'string' ? error : JSON.stringify(error)}</Alert>}
                    <Form onSubmit={handleSubmit}>
                        <Form.Group className="mb-3" controlId="bookTitle">
                            <Form.Label>Title <span className="text-danger">*</span></Form.Label>
                            <Form.Control
                                type="text" value={title} onChange={(e) => setTitle(e.target.value)} required
                                placeholder="Enter book title"
                            />
                        </Form.Group>
                        <Form.Group className="mb-3" controlId="bookAuthor">
                            <Form.Label>Author</Form.Label>
                            <Form.Control
                                type="text" value={author} onChange={(e) => setAuthor(e.target.value)}
                                placeholder="Enter author name"
                            />
                        </Form.Group>
                        <Form.Group className="mb-3" controlId="bookDescription">
                            <Form.Label>Description</Form.Label>
                            <Form.Control
                                as="textarea" rows={4} value={description} onChange={(e) => setDescription(e.target.value)}
                                placeholder="Brief description of the book"
                            />
                        </Form.Group>
                        
                        <Card className="mb-3 border-light-subtle"> {/* Bootstrap class for lighter border */}
                            <Card.Body>
                                <Form.Group controlId="bookCoverImageFile">
                                    <Form.Label className="d-flex align-items-center">
                                        <UploadCloud size={20} className="me-2 text-primary"/> Upload Cover Image (Optional)
                                    </Form.Label>
                                    <Form.Control
                                        type="file"
                                        accept="image/*"
                                        onChange={handleFileChange}
                                        disabled={isUploadingCover}
                                        className="mb-2"
                                    />
                                    {isUploadingCover && (
                                        <ProgressBar animated now={uploadProgress} label={`${uploadProgress}%`} className="mb-2" />
                                    )}
                                    {coverUploadError && <Alert variant="danger" className="mt-2 p-2 text-sm">{coverUploadError}</Alert>}
                                </Form.Group>

                                {previewUrl && (
                                    <div className="mt-3 text-center">
                                        <p className="text-muted small mb-1">Image Preview:</p>
                                        <BootstrapImage src={previewUrl} alt="Cover preview" thumbnail fluid style={{ maxHeight: '200px', border: '1px solid #ddd' }} />
                                    </div>
                                )}
                                
                                {/* <div className="text-center text-muted my-2 small">OR</div> */}

                                {/* <Form.Group controlId="bookCoverImageUrlManual">
                                    <Form.Label className="d-flex align-items-center">
                                        <ImageIcon size={20} className="me-2 text-primary"/> Enter Cover Image URL (Optional)
                                    </Form.Label>
                                    <Form.Control
                                        type="url"
                                        value={coverImageUrl} // This is now controlled by upload or manual input
                                        onChange={(e) => {
                                            setCoverImageUrl(e.target.value);
                                            setSelectedFile(null); // Clear file if URL is manually changed
                                            setPreviewUrl(null);   // Clear preview
                                            setCoverUploadError(null);
                                        }}
                                        placeholder="https://example.com/image.jpg"
                                        disabled={isUploadingCover}
                                    />
                                </Form.Group> */}
                            </Card.Body>
                        </Card>

                        <Button variant="primary" type="submit" disabled={loading || isUploadingCover} className="w-100 mt-3 py-2">
                            {loading ? <><Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" /> Creating Book...</> : 'Create Book'}
                        </Button>
                    </Form>
                </Card.Body>
            </Card>
        </Container>
    );
};

export default CreateBookPage;